import {
  type SuiOperations,
  SuiNavigationProvider,
  SuiOperationsProvider,
} from "@sylviq/ui-react-web/screens";
import { useNavigate } from "@tanstack/react-router";
import { type ReactNode, useCallback } from "react";
import { createChartAccount, listChartAccounts } from "@/lib/api";

const operations: SuiOperations = {
  async listChartAccounts() {
    const accounts = await listChartAccounts();
    return accounts.map((account) => ({
      id: account.id,
      organizationId: account.organization_id,
      legalEntityId: account.legal_entity_id,
      code: account.code,
      name: account.name,
      accountType: account.account_type,
      ...(account.parent_account_id
        ? { parentAccountId: account.parent_account_id }
        : {}),
      active: account.active,
    }));
  },
  async createChartAccount(input) {
    const created = await createChartAccount({
      code: input.code,
      name: input.name,
      account_type: input.accountType,
    });
    return {
      id: created.id,
      organizationId: created.organization_id,
      legalEntityId: created.legal_entity_id,
    };
  },
};

export function SuiOperationsHost({ children }: { children: ReactNode }) {
  return (
    <SuiOperationsProvider value={operations}>{children}</SuiOperationsProvider>
  );
}

// Generated screens navigate with concrete paths (e.g.
// `/chart-of-accounts/<id>`). The router needs the $accountId route pattern,
// so parse the id back out and hand TanStack the typed route plus its param.
// `search: (previous) => previous` keeps the Console launch scope
// (`organization_id`, `legal_entity_id`) in the URL query.
const CHART_ACCOUNT_PATH = /^\/chart-of-accounts\/([^/]+)(\/settings)?$/;

export function SuiNavigationHost({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const navigateTo = useCallback(
    (to: string) => {
      const match = CHART_ACCOUNT_PATH.exec(to);
      if (match) {
        const accountId = decodeURIComponent(match[1] ?? "");
        if (match[2]) {
          void navigate({
            to: "/chart-of-accounts/$accountId/settings",
            params: { accountId },
            search: (previous) => previous,
          });
        } else {
          void navigate({
            to: "/chart-of-accounts/$accountId",
            params: { accountId },
            search: (previous) => previous,
          });
        }
        return;
      }
      // Any other generated target: still carry the launch scope query.
      void navigate({
        to: to as never,
        search: true,
      });
    },
    [navigate],
  );
  return (
    <SuiNavigationProvider value={navigateTo}>
      {children}
    </SuiNavigationProvider>
  );
}
